import React, { useEffect, useState } from 'react'
import Request from '../../../../../../../helpers/request'
import * as url from '../../../../../../../helpers/url_helper'
import PaginationBar, { paginate, usePersistedPage } from '../../../../../../../components/Common/PaginationBar'

type LogType = 'network' | 'pcb_online' | 'switch_online'

interface LogEntry {
    id: string
    type: LogType
    timestamp: string
    sourceName: string
    eventName: string
    rawPayload: string
    remark: string
}

interface Props {
    placeId: string
    pcbs: any[]
    switches: any[]
    [key: string]: any
}

const TYPE_LABEL: Record<LogType, string> = {
    network: 'Network',
    pcb_online: 'Module Online',
    switch_online: 'Appliance Online',
}

const TYPE_BADGE: Record<LogType, React.CSSProperties> = {
    network: { background: '#EFF8FF', color: '#175CD3', border: '1px solid #B2DDFF' },
    pcb_online: { background: '#F0FDF9', color: '#107569', border: '1px solid #99F6E0' },
    switch_online: { background: '#FFF6ED', color: '#B54708', border: '1px solid #FECDAA' },
}

// Logs are denser and read in bulk, so a larger page than the default 8.
const PAGE_SIZE = 15

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC', ...style }}>
        {children}
    </td>
)

const PlaceLogs = ({ placeId, pcbs, switches }: Props) => {
    const [logs, setLogs] = useState<LogEntry[]>([])
    const [loading, setLoading] = useState(false)
    const [filterType, setFilterType] = useState<'all' | LogType>('all')
    const [filterFrom, setFilterFrom] = useState('')
    const [filterTo, setFilterTo] = useState('')
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    const [page, setPage] = usePersistedPage(`place-logs-${placeId}`)

    const placePcbs = (pcbs || []).filter((p: any) => p.placeId === placeId)
    const placeSwitches = (switches || []).filter((s: any) => s.placeId === placeId)

    useEffect(() => {
        if (!placeId) return
        setLoading(true)

        const networkReq = Request.post(url.GET_NETWORK_LOGS, { placeId, start: 0, length: 100 })
            .catch(() => null)

        const pcbReqs = Promise.all(
            placePcbs.map((pcb: any) =>
                Request.post(url.GET_ONLINE_LOGS, { moduleId: pcb.id, start: 0, length: 100 })
                    .then((res: any) => ({ pcb, data: Array.isArray(res?.data?.data) ? res.data.data : [] }))
                    .catch(() => ({ pcb, data: [] }))
            )
        )

        const swReqs = Promise.all(
            placeSwitches.map((sw: any) =>
                Request.post(url.GET_SWITCH_ONLINE_LOGS, { controlPointId: sw.id, start: 0, length: 100 })
                    .then((res: any) => ({ sw, data: Array.isArray(res?.data?.data) ? res.data.data : [] }))
                    .catch(() => ({ sw, data: [] }))
            )
        )

        Promise.all([networkReq, pcbReqs, swReqs])
            .then(([networkRes, pcbResults, swResults]) => {
                const networkData = Array.isArray(networkRes?.data?.data) ? networkRes.data.data : []
                const networkEntries: LogEntry[] = networkData.map((entry: any) => {
                    let eventName = entry.command
                    try { eventName = JSON.parse(entry.command).command || entry.command } catch {}
                    return {
                        id: entry._id,
                        type: 'network' as LogType,
                        timestamp: entry.createdAt,
                        sourceName: entry.topic || '',
                        eventName,
                        rawPayload: entry.command,
                        remark: '',
                    }
                })

                const pcbEntries: LogEntry[] = (pcbResults as any[]).flatMap(({ pcb, data }) =>
                    data.map((entry: any) => ({
                        id: entry._id,
                        type: 'pcb_online' as LogType,
                        timestamp: entry.createdAt,
                        sourceName: pcb.name || pcb.macAddress || pcb.id,
                        eventName: entry.isOnline ? 'Online' : 'Offline',
                        rawPayload: entry.remark || '',
                        remark: entry.remark || '',
                    }))
                )

                const swEntries: LogEntry[] = (swResults as any[]).flatMap(({ sw, data }) =>
                    data.map((entry: any) => ({
                        id: entry._id,
                        type: 'switch_online' as LogType,
                        timestamp: entry.createdAt,
                        sourceName: sw.name || sw.Name || sw.id,
                        eventName: entry.isOnline ? 'Online' : 'Offline',
                        rawPayload: entry.remark || '',
                        remark: entry.remark || '',
                    }))
                )

                const merged = [...networkEntries, ...pcbEntries, ...swEntries]
                    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

                setLogs(merged)
            })
            .finally(() => setLoading(false))
    }, [placeId])

    const toggleExpand = (id: string) => {
        setExpanded(prev => {
            const next = new Set(prev)
            next.has(id) ? next.delete(id) : next.add(id)
            return next
        })
    }

    const filtered = logs.filter(entry => {
        if (filterType !== 'all' && entry.type !== filterType) return false
        if (filterFrom && new Date(entry.timestamp) < new Date(filterFrom)) return false
        if (filterTo && new Date(entry.timestamp) > new Date(filterTo + 'T23:59:59')) return false
        return true
    })

    const { paged, totalPages, safePage } = paginate(filtered, page, PAGE_SIZE)

    const formatTime = (ts: string) => {
        try {
            return new Date(ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'medium' })
        } catch { return ts }
    }

    return (
        <div>
            {/* Filters */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#858D9D', marginBottom: 4 }}>Log Type</label>
                    <select
                        data-testid="filter-type"
                        value={filterType}
                        onChange={e => { setFilterType(e.target.value as any); setPage(0) }}
                        style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                    >
                        <option value="all">All</option>
                        <option value="network">Network</option>
                        <option value="pcb_online">Module Online</option>
                        <option value="switch_online">Appliance Online</option>
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#858D9D', marginBottom: 4 }}>From</label>
                    <input
                        type="date"
                        data-testid="filter-from"
                        value={filterFrom}
                        onChange={e => { setFilterFrom(e.target.value); setPage(0) }}
                        style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#858D9D', marginBottom: 4 }}>To</label>
                    <input
                        type="date"
                        data-testid="filter-to"
                        value={filterTo}
                        onChange={e => { setFilterTo(e.target.value); setPage(0) }}
                        style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                    />
                </div>
                {(filterType !== 'all' || filterFrom || filterTo) && (
                    <button
                        onClick={() => { setFilterType('all'); setFilterFrom(''); setFilterTo(''); setPage(0) }}
                        style={{ padding: '6px 12px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6, background: '#fff', cursor: 'pointer', alignSelf: 'flex-end' }}
                    >
                        Clear
                    </button>
                )}
            </div>

            {/* Table */}
            <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>Loading logs…</div>
                ) : (
                    <>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <TH>Time</TH>
                                <TH>Type</TH>
                                <TH>Source</TH>
                                <TH>Event</TH>
                                <TH>Details</TH>
                            </tr>
                        </thead>
                        <tbody>
                            {paged.length === 0 && (
                                <tr>
                                    <td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                        {logs.length === 0 ? 'No logs found for this place' : 'No logs match the current filters'}
                                    </td>
                                </tr>
                            )}
                            {paged.map(entry => (
                                <React.Fragment key={entry.id}>
                                    <tr>
                                        <TD><code style={{ fontSize: 12 }}>{formatTime(entry.timestamp)}</code></TD>
                                        <TD>
                                            <span style={{ fontSize: 12, fontWeight: 500, padding: '2px 10px', borderRadius: 12, ...TYPE_BADGE[entry.type] }}>
                                                {TYPE_LABEL[entry.type]}
                                            </span>
                                        </TD>
                                        <TD style={{ fontSize: 13, color: '#475467', maxWidth: 200 }}>{entry.sourceName}</TD>
                                        <TD><strong style={{ fontSize: 13 }}>{entry.eventName}</strong></TD>
                                        <TD>
                                            {entry.type === 'network' ? (
                                                <button
                                                    data-testid={`expand-${entry.id}`}
                                                    onClick={() => toggleExpand(entry.id)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#1a73e8', padding: 0 }}
                                                >
                                                    {expanded.has(entry.id) ? '▼ Hide' : '▶ View'}
                                                </button>
                                            ) : (
                                                <span style={{ fontSize: 13, color: '#475467' }}>{entry.remark}</span>
                                            )}
                                        </TD>
                                    </tr>
                                    {entry.type === 'network' && expanded.has(entry.id) && (
                                        <tr>
                                            <td colSpan={5} style={{ padding: '0 16px 12px', background: '#F9FAFB' }}>
                                                <pre data-testid={`raw-${entry.id}`} style={{ fontSize: 12, margin: 0, overflowX: 'auto', color: '#344054' }}>
                                                    {(() => {
                                                        try { return JSON.stringify(JSON.parse(entry.rawPayload), null, 2) }
                                                        catch { return entry.rawPayload }
                                                    })()}
                                                </pre>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    <PaginationBar
                        page={safePage}
                        totalPages={totalPages}
                        total={filtered.length}
                        pageSize={PAGE_SIZE}
                        onPage={setPage}
                    />
                    </>
                )}
            </div>
        </div>
    )
}

export default PlaceLogs
